import React, { useState, useRef, useEffect } from 'react';
import { ScrollView, LayoutChangeEvent } from 'react-native';
import { useHistory, useLocation } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { useQuery, useMutation } from '@apollo/react-hooks';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import { MuiBox } from 'theme/material-ui';
import { FontSize, FontWeight } from 'modules/styles/variables';
import { Color } from 'modules/styles/colors';
import Typography from 'components/UI/Typography';
import DefaultText from 'components/UI/Typography/DefaultText';
import Spacer from 'components/UI/Layout/Spacer';
import StyledButton from 'components/UI/Button/StyledButton';
import LinkButton from 'components/UI/Button/LinkButton';
import SpinnerModal from 'components/UI/Spinner/SpinnerModal';
import Screen from 'components/UI/Layout/Screen';
import { RootState } from 'store/types';
import { CheckListItem, VisitDetail, PatientInfo } from 'store/preRegistration/types';
import {
  currentLocationPathNameSelector,
  previousLocationPathNameSelector
} from 'store/router/selectors';
import {
  PreRegistrationVisitDetailSelector,
  PreRegistrationFetchingSelector,
  PreRegistrationCheckListStepByIdSelector,
  PreRegistrationPatientInfoSelector
} from 'store/preRegistration/selectors';
import { GetForm } from 'services/orchestration/GetForm';
import { UPDATE_PRE_REGISTRATION_CHECKLIST } from 'store/preRegistration/actions';
import { UPDATE_CHECKLIST_FORM } from 'services/orchestration/mutations';
import { StyledTextField, InitialsTextField } from './styled';
import { CenteredTypography, SkipDiv } from '../styled';
import { CheckListItemPageProps } from './types';
import { handleError, composeHtml, headerWithJWT } from '../utils';
import { errors } from '../constant';
import { PreRegHeader } from '../common';

interface MutationPayload {
  arType: string;
  checkListStepId: string;
  facilityCode: string;
  fin: string;
  mrn: string;
  stepData: {
    signature?: string;
    initials?: string;
  };
}

interface Props {
  step: CheckListItem;
  isLoading: boolean;
  visitDetail: VisitDetail;
  patientInfo: PatientInfo;
  currentUrl: string;
  referringUrl: string;
}

const CONSENT_TO_TREAT = 'CONSENTANDCONDITIONSOFSERVICE-HOSPITAL';

export const ConsentForm = ({
  step,
  isLoading,
  visitDetail,
  patientInfo,
  currentUrl,
  referringUrl
}: Props) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const fin = visitDetail?.finNumber;
  const mrn = patientInfo?.id;
  const [textState, setTextState] = useState({
    name: '',
    initials: ''
  });

  const { data, loading, error } = useQuery(GetForm, {
    variables: {
      fileName: step?.docInfo?.fileName || CONSENT_TO_TREAT,
      facilityCode: visitDetail?.facilityCode
    },
    context: {
      clientName: 'orchestration',
      headers: headerWithJWT()
    }
  });

  const [updateChecklist, { loading: updateLoading }] = useMutation(UPDATE_CHECKLIST_FORM);

  useEffect(() => {
    const { id } = location?.state || {};
    if (!id) {
      history.replace('/pre-reg/checklist');
    }
  }, []);

  const showInitials = step?.docInfo?.fileName === CONSENT_TO_TREAT;

  const onChecklistAction = async (mutationPayload: MutationPayload, eventName: AnalyticsEvent) => {
    try {
      if (step.complete) {
        handleError(errors.Complete, history);
      } else {
        const res = await updateChecklist({
          variables: {
            payload: mutationPayload
          },
          context: {
            clientName: 'orchestration',
            headers: headerWithJWT()
          }
        });

        if (res.data.updateChecklistConsentFormStep.success) {
          dispatch({
            type: UPDATE_PRE_REGISTRATION_CHECKLIST,
            payload: res.data.updateChecklistConsentFormStep.data
          });

          const amplitudeEventData: AmplitudeEventData = {
            currentUrl,
            referringUrl,
            /* eslint-disable-next-line @typescript-eslint/camelcase */
            form_location: visitDetail?.location?.name || '',
            /* eslint-disable-next-line @typescript-eslint/camelcase */
            form_detail_type: `Hospital Registration: ${step.title}`
          };
          analyticsService.logEvent(eventName, amplitudeEventData);

          history.push('/pre-reg/checklist');
        } else {
          handleError(errors[step.type], history);
        }
      }
    } catch (err) {
      handleError(errors[step.type], history);
    }
  };

  const onSkipButtonPress = () => {
    const mutationPayload = {
      arType: visitDetail.arType,
      visitType: visitDetail.type,
      checkListStepId: step.id,
      facilityCode: visitDetail.facilityCode,
      fin,
      mrn,
      stepData: {
        signature: '',
        initials: '',
        aliasId: ''
      }
    };

    onChecklistAction(mutationPayload, AnalyticsEvent.HospitalFormDetailSkipped);
  };

  const onConfirmButtonPress = () => {
    const mutationPayload = {
      arType: visitDetail.arType,
      visitType: visitDetail.type,
      checkListStepId: step.id,
      facilityCode: visitDetail.facilityCode,
      fin,
      mrn,
      stepData: {
        signature: textState.name,
        initials: textState.initials,
        aliasId: step.docInfo.docId || ''
      }
    };

    onChecklistAction(mutationPayload, AnalyticsEvent.HospitalFormDetailComplete);
  };

  const onTextChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setTextState({ ...textState, [name]: value });
  };

  const onPRaRClick = () => {
    const url =
      'https://intermountainhealthcare.org/health-information/about-your-hospital-stay/rights-and-safety/';
    window.open(url, '_blank');
  };

  const renderInitials = () => {
    if (!showInitials) {
      return null;
    }

    return (
      <MuiBox>
        <Typography fontWeight={FontWeight.semibold}>Initials</Typography>
        <MuiBox display="flex" alignItems="center">
          <InitialsTextField
            data-testid="consent-initials-input"
            name="initials"
            value={textState.initials}
            onChange={onTextChange}
            variant="outlined"
            type="text"
          />
          <Typography fontWeight={FontWeight.bold}>
            I hereby acknowledge that I have been informed of my{' '}
            <DefaultText onClick={onPRaRClick} fontSize={FontSize.base} color={Color.primary}>
              patient rights and responsibilities
            </DefaultText>
            .
          </Typography>
        </MuiBox>
        <Spacer spacing="large" />
      </MuiBox>
    );
  };

  const isConfirmDisabled = (showInitials && !textState.initials) || !textState.name;

  if (error) {
    return (
      <Screen safe>
        <CenteredTypography fontSize={FontSize.large}>
          Unable to fetch this document.
        </CenteredTypography>
      </Screen>
    );
  }

  if (isLoading || loading || updateLoading) {
    return <SpinnerModal isLoading={isLoading || loading || updateLoading} />;
  }

  return (
    <MuiBox height="100%" display="flex" flexDirection="column">
      <PreRegHeader title={step.title} />

      <ScrollView
        contentContainerStyle={{ flexGrow: 1, padding: '16px' }}
        scrollEventThrottle={400}
      >
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: composeHtml(data?.getForm?.html || 'Error in loading. Please try later')
          }}
        />
      </ScrollView>

      <MuiBox p={2}>
        <Typography fontWeight={FontWeight.semibold}>First and last name</Typography>
        <Spacer spacing="medium" />
        <StyledTextField
          data-testid="consent-name-input"
          name="name"
          value={textState.name}
          onChange={onTextChange}
          variant="outlined"
          type="text"
        />
        <Spacer spacing="large" />
        {renderInitials()}
        <StyledButton
          data-testid="consent-confirm-and-sign"
          onPress={onConfirmButtonPress}
          disabled={isConfirmDisabled}
        >
          Confirm and sign
        </StyledButton>
        <Spacer spacing="medium" />
        <SkipDiv>
          <LinkButton onPress={onSkipButtonPress}>Skip, and sign in person</LinkButton>
        </SkipDiv>
      </MuiBox>
    </MuiBox>
  );
};

const mapStateToProps = (state: RootState, props: CheckListItemPageProps) => {
  const { id } = props.location?.state || {};
  return {
    step: PreRegistrationCheckListStepByIdSelector(id)(state),
    visitDetail: PreRegistrationVisitDetailSelector(state),
    patientInfo: PreRegistrationPatientInfoSelector(state),
    isLoading: PreRegistrationFetchingSelector(state),
    currentUrl: currentLocationPathNameSelector(state),
    referringUrl: previousLocationPathNameSelector(state)
  };
};

export default connect(mapStateToProps)(ConsentForm);
